import type { PreviewTemplate } from '../components/TemplatePreview';
import {
  extractBodyText,
  extractButtons,
  extractCarouselCards,
  extractHeaderFormat,
  detectBodyVariables,
  type RawTemplate,
} from './template-introspection';

/**
 * Builds the exact `PreviewTemplate` shape `<TemplatePreview>` renders, from
 * a template's raw Meta `components[]` (the static, approved structure)
 * overlaid with per-send or per-node dynamic values. Shared by the
 * flow-builder's `FlowTemplatePanel` live preview and the real chat bubble's
 * `parseSentTemplate` (chat.tsx) so both go through identical mapping —
 * "the exact preview shown during node generation" the user asked for.
 */
export interface TemplatePreviewOverlay {
  bodyVariables?: string[];
  headerMediaUrl?: string | null;
  headerText?: string;
  limitedTimeOfferExpiresAtMs?: number;
  carousel?: Array<{ mediaUrl?: string | null; bodyVariables?: string[] }>;
}

const HEADER_FORMATS = ['NONE', 'TEXT', 'IMAGE', 'VIDEO', 'GIF', 'DOCUMENT', 'LOCATION'] as const;
type HeaderFormat = (typeof HEADER_FORMATS)[number];

export function buildPreviewTemplate(
  rawTemplate: { components?: unknown[] | null; category?: string | null } | null | undefined,
  overlay: TemplatePreviewOverlay,
): PreviewTemplate | null {
  if (!rawTemplate?.components) return null;
  const t = rawTemplate as RawTemplate;

  const bodyText = extractBodyText(t) ?? '';
  const rawHeaderFormat = (extractHeaderFormat(t) ?? 'NONE').toUpperCase();
  const headerFormat = (HEADER_FORMATS as readonly string[]).includes(rawHeaderFormat)
    ? (rawHeaderFormat as HeaderFormat)
    : 'NONE';
  const buttons = extractButtons(t);
  const detected = detectBodyVariables(bodyText);

  const components = (rawTemplate.components ?? []) as Array<{
    type?: string;
    text?: string;
    limited_time_offer?: { text?: string };
  }>;
  const footerText = components.find((c) => (c.type ?? '').toUpperCase() === 'FOOTER')?.text;
  const ltoComponent = components.find(
    (c) => (c.type ?? '').toUpperCase() === 'LIMITED_TIME_OFFER',
  );

  const category: PreviewTemplate['category'] =
    rawTemplate.category === 'AUTHENTICATION' || rawTemplate.category === 'MARKETING'
      ? rawTemplate.category
      : 'UTILITY';

  const rawCards = extractCarouselCards(t);
  const carouselCards = rawCards
    ? rawCards.map((card, i) => ({
        headerFormat: card.headerFormat,
        mediaPreviewUrl: overlay.carousel?.[i]?.mediaUrl ?? null,
        bodyText: applyPositional(card.bodyText, overlay.carousel?.[i]?.bodyVariables),
        buttons: card.buttons,
      }))
    : undefined;

  return {
    category,
    header:
      headerFormat === 'NONE'
        ? undefined
        : {
            format: headerFormat,
            text: overlay.headerText,
            mediaPreviewUrl: overlay.headerMediaUrl ?? undefined,
          },
    bodyText,
    bodyExamples: overlay.bodyVariables,
    bodyVariableNames: detected.format === 'named' ? detected.names : undefined,
    parameterFormat: detected.format === 'named' ? 'NAMED' : 'POSITIONAL',
    footerText,
    buttons: buttons.map((b) => ({ type: b.type, text: b.text, url: b.url })),
    limitedTimeOffer: ltoComponent
      ? {
          text: ltoComponent.limited_time_offer?.text || 'Limited-time offer',
          hasExpiration: overlay.limitedTimeOfferExpiresAtMs !== undefined,
        }
      : undefined,
    carouselCards,
  };
}

function applyPositional(
  text: string | undefined,
  values: string[] | undefined,
): string | undefined {
  if (!text) return undefined;
  if (!values || values.length === 0) return text;
  return text.replace(/\{\{(\d+)\}\}/g, (_, idx: string) => values[Number(idx) - 1] ?? '');
}

/**
 * Mirrors `SentTemplateSnapshot` from
 * `apps/api/src/modules/whatsapp/services/template-render-snapshot.ts` —
 * the "what was actually sent" payload stored on a template message's
 * `dataJson.sentTemplate`. Kept as a local, loosely-typed mirror (same
 * convention as this file's sibling `parse*` helpers in chat.tsx) since it
 * crosses the dataJson free-form boundary, not a typed API response.
 */
export interface SentTemplateSnapshot {
  templateName: string;
  languageCode: string;
  components: unknown[] | null;
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION' | null;
  renderedBodyText: string | null;
  rendered: {
    bodyVariables?: string[];
    header?: {
      kind: string;
      mediaUrl?: string | null;
      text?: string;
    };
    limitedTimeOfferExpiresAtMs?: number;
    carousel?: Array<{ mediaUrl?: string | null; bodyVariables?: string[] }>;
  };
}

/**
 * `AUTHENTICATION` templates are skipped — `<TemplatePreview>`'s OTP
 * rendering is an authoring-time placeholder ("Your verification code is
 * 123-456"), not the real code, so the plain-text `message` column (already
 * the true rendered body) is the correct fallback there.
 */
export function buildPreviewFromSentTemplate(
  snapshot: SentTemplateSnapshot | null | undefined,
): PreviewTemplate | null {
  if (!snapshot || !snapshot.components || snapshot.category === 'AUTHENTICATION') return null;
  return buildPreviewTemplate(
    { components: snapshot.components, category: snapshot.category },
    {
      bodyVariables: snapshot.rendered.bodyVariables,
      headerMediaUrl: snapshot.rendered.header?.mediaUrl,
      headerText: snapshot.rendered.header?.text,
      limitedTimeOfferExpiresAtMs: snapshot.rendered.limitedTimeOfferExpiresAtMs,
      carousel: snapshot.rendered.carousel,
    },
  );
}
